import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { getSubmissionByID, updateSubmissionStatus, updateSubmissionComment, getFile, uploadFile, deleteFile } from '../services/api.service';
import { useAuth } from '../services/auth.service';
import StatusDropdown from '../components/status-dropdown';
import TransactionComponent from '../components/transaction';
import { FaFileImage, FaFilePdf, FaFileAlt, FaTrash, FaUpload, FaChevronLeft } from 'react-icons/fa';
import { FiExternalLink } from "react-icons/fi";
import moment from 'moment';

const statusTranslations = {
    'paid': 'Créé',
    'in-progress': 'En cours',
    'completed': 'Complété',
    'rejected': 'Rejeté',
    'fileDeleted': 'Fichier supprimé',
    'fileAdded': 'Fichier ajouté',
    'comment': 'Commentaire'
};

const pickI18n = (obj, lang = 'fr') => obj?.[lang] || obj?.fr || obj?.en || obj?.de || '';

function SubmissionDetailPage() {
    const { id } = useParams();
    const location = useLocation();
    const dashboardState = location.state?.dashboardState;
    const [submission, setSubmission] = useState(null);
    const { user } = useAuth();
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileToDelete, setFileToDelete] = useState(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [comment, setComment] = useState("");

    useEffect(() => {
        const fetchSubmission = async () => {
            try {
                const response = await getSubmissionByID(id);
                setSubmission(response);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchSubmission();
    }, [id]);

    const renderFileIcon = (mimeType) => {
        if (mimeType?.startsWith('image/')) return <FaFileImage className="text-2xl text-blue-500" />;
        if (mimeType === 'application/pdf') return <FaFilePdf className="text-2xl text-red-500" />;
        return <FaFileAlt className="text-2xl text-gray-500" />;
    };

    const handleStatusChange = async (submissionId, newStatus, comment = null) => {
        try {
            await updateSubmissionStatus(submissionId, newStatus, comment, { firstName: user.firstName, lastName: user.lastName, comment });
            const updatedSubmission = await getSubmissionByID(submissionId);
            setSubmission(updatedSubmission);
            setComment("");
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const handleCommentSave = async () => {
        try {
            await updateSubmissionComment(id, comment, { firstName: user.firstName, lastName: user.lastName });
            const updatedSubmission = await getSubmissionByID(id);
            setSubmission(updatedSubmission);
            setComment("");
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const handleFileDelete = async () => {
        try {
            await deleteFile(id, fileToDelete, { firstName: user.firstName, lastName: user.lastName });
            const updatedSubmission = await getSubmissionByID(id);
            setSubmission(updatedSubmission);
            setShowDeleteConfirmation(false);
            setFileToDelete(null);
        } catch (error) {
            console.error('Error deleting file:', error);
        }
    };

    const confirmFileDelete = (filePath) => {
        setFileToDelete(filePath);
        setShowDeleteConfirmation(true);
    };

    const handleFileUpload = async (event) => {
        event.preventDefault();
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append('adminFile', selectedFile);
        formData.append('admin', JSON.stringify({ firstName: user.firstName, lastName: user.lastName }));

        try {
            await uploadFile(id, formData);
            const updatedSubmission = await getSubmissionByID(id);
            setSubmission(updatedSubmission);
            setSelectedFile(null);
        } catch (error) {
            console.error('Error uploading file:', error);
        }
    };

    // ======== Build admin field view ========
    const {
        customer = {},
        billing = {},
        services = [],
        files = [],
        history = [],
        fields: submittedFields = {}
    } = submission || {};

    // index definitions by key + count per service for general/specific grouping
    const { defByKey, countByKey, keysByServiceId } = useMemo(() => {
        const dMap = new Map();         // key -> def (first occurrence)
        const counts = new Map();       // key -> number of services containing it
        const perSvc = new Map();       // svcId -> Set(keys)

        (services || []).forEach(svc => {
            const set = new Set();
            (svc.requiredFields || []).forEach(def => {
                if (!dMap.has(def.key)) dMap.set(def.key, def);
                if (!counts.has(def.key)) counts.set(def.key, 0);
                // count per service only once
                if (!set.has(def.key)) {
                    set.add(def.key);
                    counts.set(def.key, (counts.get(def.key) || 0) + 1);
                }
            });
            perSvc.set(svc._id, set);
        });

        return { defByKey: dMap, countByKey: counts, keysByServiceId: perSvc };
    }, [services]);

    // general keys = keys present in >1 services
    const generalKeySet = useMemo(() => {
        const set = new Set();
        for (const [k, c] of countByKey.entries()) {
            if (c > 1) set.add(k);
        }
        return set;
    }, [countByKey]);

    // Prepare grouped sections to render
    const fieldSections = useMemo(() => {
        const entries = Object.entries(submittedFields || {});
        if (!entries.length) return [];

        // GENERAL
        const general = entries
            .filter(([k]) => generalKeySet.has(k))
            .map(([k, v]) => ({ key: k, value: v, def: defByKey.get(k) }))
            .sort((a, b) => (a.def?.order ?? 0) - (b.def?.order ?? 0));

        // PER SERVICE
        const perService = (services || []).map(svc => {
            const svcKeys = keysByServiceId.get(svc._id) || new Set();
            const items = entries
                .filter(([k]) => svcKeys.has(k) && !generalKeySet.has(k))
                .map(([k, v]) => ({ key: k, value: v, def: defByKey.get(k) }))
                .sort((a, b) => (a.def?.order ?? 0) - (b.def?.order ?? 0));
            return { svc, items };
        }).filter(sec => sec.items.length > 0);

        // UNKNOWN (submitted but not in any definition)
        const knownKeys = new Set([...defByKey.keys()]);
        const unknown = entries
            .filter(([k]) => !knownKeys.has(k))
            .map(([k, v]) => ({ key: k, value: v, def: null }));

        const sections = [];
        if (general.length) {
            sections.push({
                title: 'Informations générales',
                items: general
            });
        }
        perService.forEach(sec => {
            sections.push({
                title: `Informations pour : ${pickI18n(sec.svc.title, 'fr')}`,
                items: sec.items
            });
        });
        if (unknown.length) {
            sections.push({
                title: 'Autres informations',
                items: unknown
            });
        }
        return sections;
    }, [submittedFields, services, defByKey, generalKeySet, keysByServiceId]);

    const totalServicesAmount = useMemo(() => {
        return (services || []).reduce((acc, service) => {
            return acc + Number(service?.price || 0);
        }, 0);
    }, [services]);

    const isFreeSubmission = totalServicesAmount <= 0 || submission?.paymentType === 'free';

    const formatFieldValue = (value, def) => {
        if (value === null || value === undefined || value === '') return <span className="italic text-gray-500">Non renseigné</span>;

        const type = def?.type || 'text';
        if (type === 'checkbox') return value ? 'Oui' : 'Non';
        if (type === 'date') {
            // try to format DD/MM/YYYY safely
            const m = moment(value, ['YYYY-MM-DD', moment.ISO_8601, 'DD/MM/YYYY'], true);
            return m.isValid() ? m.format('DD/MM/YYYY') : String(value);
        }
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'object') {
            // avoid dumping huge object
            try { return JSON.stringify(value); } catch { return String(value); }
        }
        return String(value);
    };

    const uniqueFiles = useMemo(() => {
        const seen = new Set();
        return (files || []).filter(file => {
            const key = file.originalName || file.path;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [files]);

    if (!submission) return <div>Loading...</div>;

    return (
        <div>
            <div className="container mx-auto p-4">
                <div className='text-left flex flex-row w-11/12 mb-6'>
                    <Link
                        to="/"
                        state={dashboardState ? { dashboardState } : undefined}
                        className='flex flex-row items-center gap-2 hover:underline transition duration-150'
                    >
                        <FaChevronLeft /> Retour
                    </Link>
                </div>
                <div className='flex w-11/12 gap-12 mb-24'>
                    <div className="w-3/5 h-fit">
                        <div className='bg-white rounded-lg shadow-md p-6 mb-6'>
                            <div className="flex justify-between items-start mb-4">
                                <h2 className="text-2xl font-bold">
                                    Dossier n°{submission.societyPrefix ? `${submission.societyPrefix}-` : ''}
                                    {submission.dossierNumber || submission.transactionNumber || '-'}
                                </h2>
                                <div className='flex flex-col gap-4'>
                                    <span className="text-sm italic text-right text-gray-600">
                                        Créé le : {moment(submission.createdAt).format('DD/MM/YYYY à HH:mm')}
                                    </span>
                                    <Link to={'/user/' + submission.customer.email}>
                                        <button className="flex items-center gap-1 px-3 py-1 text-sm text-gray-500 border border-gray-300 rounded hover:bg-gray-100 transition-colors">
                                            <FiExternalLink className="w-3 h-3" />
                                            Voir toutes les demandes de cet utilisateur
                                        </button>
                                    </Link>
                                </div>
                            </div>

                            <h3 className="text-xl mb-4 underline">Informations de l'utilisateur :</h3>
                            <p><strong>Nom :</strong> {customer.lastname}</p>
                            <p><strong>Prénom :</strong> {customer.firstname}</p>
                            <p><strong>Email :</strong> {customer.email}</p>
                            <p><strong>Téléphone :</strong> {customer.phone}</p>
                            <p><strong>Société :</strong> {billing.companyName || "Non spécifié"}</p>
                            {submission.subdomain && <p><strong>Sous-domaine :</strong> {submission.subdomain}</p>}
                            {customer.ssn && <p><strong>SSN :</strong> {customer.ssn}</p>}
                            <br />
                            <p><strong>Adresse :</strong> {billing.address}</p>
                            {billing.address2 && <p><strong>Complément d'adresse :</strong> {billing.address2}</p>}
                            <p><strong>Code postal :</strong> {billing.postalCode}</p>
                            <p><strong>Ville :</strong> {billing.city}</p>
                            <p><strong>Pays :</strong> {billing.country}</p>
                            <p><strong>Numéro de TVA :</strong> {billing.VATNumber ? billing.VATNumber : "Non spécifié"}</p>
                            <br />
                            <div className="mt-4">
                                <label htmlFor="comment" className="block text-sm font-medium text-gray-700">Commentaire</label>
                                <textarea
                                    id="comment"
                                    rows="4"
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    placeholder="Ajouter un commentaire ici"
                                />
                                <button
                                    type="submit"
                                    disabled={!comment}
                                    onClick={() => handleCommentSave()}
                                    className="mx-auto my-4 flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
                                >
                                    Sauvegarder le commentaire
                                </button>
                            </div>
                        </div>

                        {/* ====== NEW: Fields provided by user ====== */}
                        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                            <h3 className="text-xl font-bold mb-4">Informations fournies par l'utilisateur</h3>

                            {!Object.keys(submittedFields || {}).length ? (
                                <p className="text-sm text-gray-600">Aucune information additionnelle fournie.</p>
                            ) : (
                                <div className="space-y-8">
                                    {fieldSections.map((sec, idx) => (
                                        <div key={idx} className="space-y-3">
                                            <h4 className="text-lg font-semibold text-primary">{sec.title}</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {sec.items.map(({ key, value, def }) => {
                                                    const label = def ? pickI18n(def.label, 'fr') || key : key;
                                                    return (
                                                        <div key={key} className="rounded border border-gray-200 p-3">
                                                            <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
                                                            <div className="mt-1 text-sm text-gray-900">
                                                                {formatFieldValue(value, def)}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h3 className="text-xl font-bold mb-4">Historique des changements</h3>
                            <table className="min-w-full bg-white border border-gray-300">
                                <thead>
                                    <tr>
                                        <th className="py-2 px-4 border-b">Prénom</th>
                                        <th className="py-2 px-4 border-b">Nom</th>
                                        <th className="py-2 px-4 border-b">Date et heure</th>
                                        <th className="py-2 px-4 border-b">Statut</th>
                                    </tr>
                                </thead>
                                {submission.history.length === 0 ? (
                                    <tbody>
                                        <tr>
                                            <td colSpan="4" className="text-center py-4">
                                                <p>Aucun historique disponible.</p>
                                            </td>
                                        </tr>
                                    </tbody>
                                ) : (
                                    <tbody>
                                        {history
                                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                                            .map((h, index) => (
                                                <React.Fragment key={index}>
                                                    <tr className="hover:bg-gray-100">
                                                        <td className={`py-2 px-4 border-b ${h.comment ? 'border-white' : ''}`}>
                                                            {h.firstName}
                                                        </td>
                                                        <td className={`py-2 px-4 border-b ${h.comment ? 'border-white' : ''}`}>
                                                            {h.lastName}
                                                        </td>
                                                        <td className={`py-2 px-4 border-b ${h.comment ? 'border-white' : ''}`}>
                                                            {moment(h.date).format('DD/MM/YYYY à HH:mm')}
                                                        </td>
                                                        <td className={`py-2 px-4 border-b ${h.comment ? 'border-white' : ''}`}>
                                                            {statusTranslations[h.status]}
                                                        </td>
                                                    </tr>
                                                    {h.comment && (
                                                        <tr className="hover:bg-gray-100">
                                                            <td colSpan="4" className="py-2 px-4 border-b">{h.comment}</td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                    </tbody>
                                )}
                            </table>
                        </div>
                    </div>

                    <div className='w-2/5 flex flex-col gap-8'>
                        <section className="bg-white shadow rounded p-6">
                            <h2 className="text-xl font-semibold mb-4">Service{services.length > 1 ? "s" : ""} acheté{services.length > 1 ? "s" : ""}</h2>
                            <ul className="list-disc list-inside space-y-2">
                                {services.map(s => (
                                    <li key={s._id}>
                                        {pickI18n(s.title, 'fr')} — {Number(s.price || 0).toFixed(2)}€
                                    </li>
                                ))}
                            </ul>
                        </section>

                        <div className="w-full h-fit bg-white rounded-lg shadow-md p-6 mb-6">
                            <h3 className="text-xl font-semibold mb-4">Document{files.length > 1 ? "s" : ""} de l'utilisateur</h3>
                            <ul className="list-disc list-inside mb-4">
                                {uniqueFiles.map(file => (
                                    <li
                                        key={file._id || file.path || file.originalName}
                                        className="flex items-center justify-between space-x-2 pb-2"
                                    >
                                        <div className="flex gap-2">
                                            {renderFileIcon(file.mimeType)}
                                            <div className="max-w-[300px] truncate text-blue-500 hover:underline">
                                                <a
                                                    href={getFile(file.path)}
                                                    download
                                                    className="text-blue-500 hover:underline"
                                                >
                                                    {file.originalName}
                                                </a>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => confirmFileDelete(file.path)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <FaTrash />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            <form onSubmit={(e) => handleFileUpload(e, selectedFile)} className="space-y-4 flex flex-col items-center">
                                <div className='w-full'>
                                    <label
                                        htmlFor="file-upload"
                                        className="flex items-center justify-center px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors w-full"
                                    >
                                        <FaUpload className="mr-2" />
                                        {selectedFile ? "Modifier le document" : "Ajouter un document"}
                                        <input
                                            id="file-upload"
                                            type="file"
                                            className="hidden"
                                            onChange={(e) => setSelectedFile(e.target.files[0])}
                                        />
                                    </label>
                                    {selectedFile && (
                                        <p className="max-w-[400px] mt-2 text-sm text-gray-700 truncate">
                                            Fichier sélectionné : <strong>{selectedFile.name}</strong>
                                        </p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    disabled={!selectedFile}
                                    className={`flex items-center px-4 py-2 rounded-lg text-white transition-colors ${selectedFile
                                        ? "bg-primary hover:bg-primary-dark"
                                        : "bg-gray-400 cursor-not-allowed"
                                        }`}
                                >
                                    <FaUpload className="mr-2" /> Importer
                                </button>
                            </form>
                        </div>

                        <div className="w-full h-fit bg-white rounded-lg shadow-md p-6 mb-6">
                            <div className='flex items-center justify-between'>
                                <strong>Statut de la demande :</strong>
                                <StatusDropdown submissionId={id} currentStatus={submission.status} onStatusChange={handleStatusChange} />
                            </div>
                            {(() => {
                                const lastStatusChange = [...submission.history]
                                    .reverse()
                                    .find(h => h.status && h.firstName && h.lastName);
                                return lastStatusChange ? (
                                    <div className="text-xs text-gray-500 italic">
                                        Traité par : {lastStatusChange.firstName} {lastStatusChange.lastName}
                                    </div>
                                ) : null;
                            })()}
                        </div>

                        <div className="w-full h-fit bg-white rounded-lg shadow-md mb-6">
                            {isFreeSubmission ? (
                                <div className="p-6">
                                    <h3 className="text-xl font-semibold mb-3">Facture</h3>

                                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800">
                                        <p className="font-medium">
                                            Aucune facture générée
                                        </p>
                                        <p className="text-sm mt-1">
                                            Cette demande concerne une prestation gratuite. Aucun paiement n’a été effectué et aucune facture n’est disponible.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <TransactionComponent transactionId={submission._id} />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showDeleteConfirmation && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg">
                        <p>Êtes-vous sûr de vouloir supprimer ce fichier ?</p>
                        <div className="mt-4 flex justify-end gap-4">
                            <button
                                onClick={() => setShowDeleteConfirmation(false)}
                                className="px-4 py-2 bg-gray-300 rounded-lg"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleFileDelete}
                                className="px-4 py-2 bg-red-500 text-white rounded-lg"
                            >
                                Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SubmissionDetailPage;

// import React, { useEffect, useMemo, useState } from "react";
// import { Link, useParams } from "react-router-dom";
// import moment from "moment";
// import { FiExternalLink } from "react-icons/fi";

// import {
//     getSubmissionByID,
//     updateSubmissionComment,
//     getFile,
//     updateServiceStatus,
//     updateServiceDocument,
//     addServiceDocument,
//     deleteServiceDocument,
//     getTransactionPdf,
// } from "../services/api.service";

// import { useAuth } from "../services/auth.service";

// /* ────────────────────────────────────────────────────────────────────────────
//  * UI primitives (matching the mock)
//  * ──────────────────────────────────────────────────────────────────────────── */
// const STATUS_LABELS = {
//     // Docs
//     pending: { label: "En attente", classes: "bg-gray-100 text-gray-700" },
//     approved: { label: "Validé", classes: "bg-green-100 text-green-800" },
//     rejected: { label: "Refusé", classes: "bg-red-100 text-red-800" },
//     // Services
//     in_review: { label: "En cours", classes: "bg-blue-100 text-blue-800" },
//     completed: { label: "Complété", classes: "bg-emerald-100 text-emerald-800" },
// };

// const REJECTION_REASONS = [
//     { id: "blurry", label: "Document flou ou illisible" },
//     { id: "expired", label: "Document expiré" },
//     { id: "mismatch", label: "Informations non concordantes" },
//     { id: "incomplete", label: "Document incomplet ou pages manquantes" },
//     { id: "wrong_type", label: "Mauvais type de document" },
// ];

// function cx(...arr) {
//     return arr.filter(Boolean).join(" ");
// }

// function StatusBadge({ status }) {
//     const s = STATUS_LABELS[status] || { label: status, classes: "bg-gray-100 text-gray-700" };
//     return (
//         <span className={cx("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium", s.classes)}>
//             {s.label}
//         </span>
//     );
// }

// function computeGlobalStatus(services = []) {
//     const anyRejected = services.some(
//         (s) => s.status === "rejected" || (s.documents || []).some((d) => d.status === "rejected")
//     );
//     if (anyRejected) return { label: "Action requise", classes: "bg-red-100 text-red-800" };

//     const anyPending = services.some(
//         (s) =>
//             (s.status || "pending") === "pending" ||
//             (s.documents || []).some((d) => (d.status || "pending") === "pending")
//     );
//     if (anyPending) return { label: "En cours de vérification", classes: "bg-amber-100 text-amber-800" };

//     return { label: "Dossier complété", classes: "bg-emerald-100 text-emerald-800" };
// }

// function GlobalBadge({ services }) {
//     const g = computeGlobalStatus(services);
//     return (
//         <span className={cx("inline-flex items-center rounded-full px-3 py-1 text-sm font-medium", g.classes)}>
//             {g.label}
//         </span>
//     );
// }

// function IconButton({ title, onClick, children }) {
//     return (
//         <button
//             type="button"
//             onClick={onClick}
//             title={title}
//             className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-gray-50 active:scale-[.99]"
//         >
//             {children}
//         </button>
//     );
// }

// function Section({ title, right, children }) {
//     return (
//         <section className="rounded-2xl border bg-white p-4 shadow-sm">
//             <div className="mb-3 flex items-center justify-between">
//                 <h3 className="text-base font-semibold">{title}</h3>
//                 {right}
//             </div>
//             {children}
//         </section>
//     );
// }

// /* ────────────────────────────────────────────────────────────────────────────
//  * Reject dialog
//  * ──────────────────────────────────────────────────────────────────────────── */
// function RejectDialog({ open, onClose, onConfirm, defaultEmail }) {
//     const [reason, setReason] = useState(REJECTION_REASONS[0].id);
//     const [note, setNote] = useState("");
//     const [sendMail, setSendMail] = useState(true);
//     if (!open) return null;
//     return (
//         <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4">
//             <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
//                 <div className="mb-3 flex items-center justify-between">
//                     <h4 className="text-lg font-semibold">Refuser le document</h4>
//                     <button onClick={onClose} className="rounded-lg p-2 hover:bg-gray-50">✕</button>
//                 </div>
//                 <div className="space-y-3">
//                     <label className="block text-sm font-medium">Motif</label>
//                     <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full rounded-xl border px-3 py-2">
//                         {REJECTION_REASONS.map((r) => (
//                             <option key={r.id} value={r.id}>{r.label}</option>
//                         ))}
//                     </select>
//                     <label className="block text-sm font-medium">Message (optionnel)</label>
//                     <textarea
//                         value={note}
//                         onChange={(e) => setNote(e.target.value)}
//                         rows={4}
//                         className="w-full resize-none rounded-xl border px-3 py-2"
//                         placeholder="Précisions à envoyer au client…"
//                     />
//                     <label className="flex items-center gap-2 text-sm">
//                         <input type="checkbox" className="h-4 w-4" checked={sendMail} onChange={(e) => setSendMail(e.target.checked)} />
//                         Informer le client par e-mail ({defaultEmail})
//                     </label>
//                 </div>
//                 <div className="mt-5 flex justify-end gap-2">
//                     <button onClick={onClose} className="rounded-xl border px-4 py-2 text-sm">Annuler</button>
//                     <button
//                         onClick={() => onConfirm({ reason, note, sendMail })}
//                         className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
//                     >
//                         Confirmer le refus
//                     </button>
//                 </div>
//             </div>
//         </div>
//     );
// }

// /* ────────────────────────────────────────────────────────────────────────────
//  * Document row
//  * ──────────────────────────────────────────────────────────────────────────── */
// function DocumentRow({ doc, onApprove, onReject, onPreview, onDelete }) {
//     return (
//         <div className="grid grid-cols-12 items-center gap-2 border-b py-2 last:border-none">
//             <div className="col-span-6 truncate">
//                 <div className="font-medium truncate">{doc.name || doc.originalName}</div>
//                 <div className="text-xs text-gray-500">{doc.type || doc.mimeType}</div>
//             </div>
//             <div className="col-span-2">
//                 <StatusBadge status={doc.status || "pending"} />
//             </div>
//             <div className="col-span-4 flex justify-end gap-2">
//                 <IconButton title="Prévisualiser" onClick={() => onPreview?.(doc)}>
//                     <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
//                         <path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 12a5 5 0 110-10 5 5 0 010 10zm0-2.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
//                     </svg>
//                     Voir
//                 </IconButton>
//                 <IconButton title="Valider" onClick={() => onApprove?.(doc)}>
//                     <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
//                         <path d="M9 16.2l-3.5-3.5L4 14.2 9 19l12-12-1.5-1.5z" />
//                     </svg>
//                     Valider
//                 </IconButton>
//                 <IconButton title="Refuser" onClick={() => onReject?.(doc)}>
//                     <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
//                         <path d="M18.3 5.7L12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7 4.3 4.3l6.3 6.3 6.3-6.3z" />
//                     </svg>
//                     Refuser
//                 </IconButton>
//                 {onDelete && (
//                     <IconButton title="Supprimer" onClick={() => onDelete?.(doc)}>
//                         <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
//                             <path d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v9h-2V9zm4 0h2v9h-2V9zM7 9h2v9H7V9z" />
//                         </svg>
//                         Supprimer
//                     </IconButton>
//                 )}
//             </div>
//         </div>
//     );
// }

// /* ────────────────────────────────────────────────────────────────────────────
//  * Service card (collapsible)
//  * ──────────────────────────────────────────────────────────────────────────── */
// function ServiceCard({ service, onChangeDoc, onChangeStatus, onAddDoc, onDeleteDoc, clientEmail }) {
//     const [open, setOpen] = useState(true);
//     const [rejectTarget, setRejectTarget] = useState(null);
//     const [newDocFile, setNewDocFile] = useState(null);

//     const docs = service.documents || [];
//     const approvedCount = docs.filter((d) => d.status === "approved").length;
//     const pendingCount = docs.filter((d) => (d.status || "pending") === "pending").length;

//     return (
//         <div className="rounded-2xl border bg-white shadow-sm">
//             {/* Header */}
//             <button className="w-full rounded-t-2xl p-4 text-left hover:bg-gray-50" onClick={() => setOpen((v) => !v)}>
//                 <div className="flex items-center justify-between">
//                     <div>
//                         <div className="text-sm text-gray-500">Prestation</div>
//                         <h4 className="text-lg font-semibold">{service.title?.fr || service.title || "Prestation"}</h4>
//                     </div>
//                     <div className="flex items-center gap-3">
//                         <StatusBadge status={service.status || "pending"} />
//                         <span className="text-xs text-gray-500">
//                             {approvedCount}/{docs.length} docs validés{pendingCount ? ` • ${pendingCount} en attente` : ""}
//                         </span>
//                         <svg className={cx("h-4 w-4 transition", open ? "rotate-180" : "rotate-0")} viewBox="0 0 20 20" fill="currentColor">
//                             <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
//                         </svg>
//                     </div>
//                 </div>
//             </button>

//             {open && (
//                 <div className="border-t p-4">
//                     {/* Service status */}
//                     <div className="mb-4 flex flex-wrap items-center gap-2">
//                         <span className="text-sm text-gray-500">Statut de la prestation :</span>
//                         <select
//                             value={service.status || "pending"}
//                             onChange={(e) => onChangeStatus(e.target.value)}
//                             className="rounded-xl border px-3 py-2 text-sm"
//                         >
//                             <option value="pending">En attente</option>
//                             <option value="in_review">En cours</option>
//                             <option value="completed">Complété</option>
//                             <option value="rejected">Refusé</option>
//                         </select>
//                     </div>

//                     {/* Per-service uploader */}
//                     {onAddDoc && (
//                         <form
//                             className="mb-4 flex items-center gap-2"
//                             onSubmit={(e) => {
//                                 e.preventDefault();
//                                 if (newDocFile) {
//                                     onAddDoc?.(newDocFile);
//                                     setNewDocFile(null);
//                                 }
//                             }}
//                         >
//                             <input
//                                 type="file"
//                                 onChange={(e) => setNewDocFile(e.target.files?.[0] || null)}
//                                 className="w-full rounded-xl border px-3 py-2 text-sm"
//                             />
//                             <button
//                                 disabled={!newDocFile}
//                                 className={cx(
//                                     "rounded-xl px-3 py-2 text-sm text-white",
//                                     newDocFile ? "bg-gray-900 hover:bg-black" : "bg-gray-400 cursor-not-allowed"
//                                 )}
//                             >
//                                 Ajouter
//                             </button>
//                         </form>
//                     )}

//                     {/* Documents table */}
//                     <div className="rounded-xl border">
//                         <div className="grid grid-cols-12 gap-2 border-b bg-gray-50 p-2 text-xs font-medium text-gray-600">
//                             <div className="col-span-6">Document</div>
//                             <div className="col-span-2">Statut</div>
//                             <div className="col-span-4 text-right">Actions</div>
//                         </div>
//                         <div className="p-2">
//                             {docs.length === 0 && (
//                                 <div className="p-3 text-sm text-gray-500">Aucun document lié à cette prestation.</div>
//                             )}
//                             {docs.map((doc) => (
//                                 <DocumentRow
//                                     key={doc.id || doc._id || doc.path}
//                                     doc={doc}
//                                     onPreview={() => window.open(doc.url || (doc.path ? getFile(doc.path) : "#"), "_blank")}
//                                     onApprove={() => onChangeDoc(doc, { status: "approved", reason: null, note: null })}
//                                     onReject={() => setRejectTarget(doc)}
//                                     onDelete={onDeleteDoc ? () => onDeleteDoc(doc) : undefined}
//                                 />
//                             ))}
//                         </div>
//                     </div>
//                 </div>
//             )}

//             <RejectDialog
//                 open={!!rejectTarget}
//                 defaultEmail={clientEmail}
//                 onClose={() => setRejectTarget(null)}
//                 onConfirm={({ reason, note, sendMail }) => {
//                     const doc = rejectTarget;
//                     setRejectTarget(null);
//                     onChangeDoc(doc, { status: "rejected", reason, note, sendMail });
//                 }}
//             />
//         </div>
//     );
// }

// /* ────────────────────────────────────────────────────────────────────────────
//  * Main page
//  * ──────────────────────────────────────────────────────────────────────────── */
// export default function SubmissionReviewPage() {
//     const { id } = useParams();
//     const { user } = useAuth();

//     const [submission, setSubmission] = useState(null);
//     const [comment, setComment] = useState("");

//     useEffect(() => {
//         (async () => {
//             try {
//                 const data = await getSubmissionByID(id);
//                 setSubmission(data);
//             } catch (e) {
//                 console.error(e);
//             }
//         })();
//     }, [id]);

//     const services = submission?.services || [];

//     const lastHandler = useMemo(() => {
//         const h = (submission?.history || [])
//             .slice()
//             .reverse()
//             .find((x) => x.firstName && x.lastName);
//         return h ? `${h.firstName} ${h.lastName}` : null;
//     }, [submission?.history]);

//     // ===== Actions =====
//     const refresh = async () => {
//         const fresh = await getSubmissionByID(id);
//         setSubmission(fresh);
//     };

//     const handleSaveComment = async () => {
//         try {
//             await updateSubmissionComment(id, comment, { firstName: user.firstName, lastName: user.lastName });
//             await refresh();
//             setComment("");
//         } catch (e) {
//             console.error(e);
//         }
//     };

//     const handleChangeServiceStatus = async (serviceId, status) => {
//         try {
//             await updateServiceStatus(id, serviceId, status, { firstName: user.firstName, lastName: user.lastName });
//             // optimistic
//             setSubmission((prev) => ({
//                 ...prev,
//                 services: prev.services.map((s) => (s._id === serviceId || s.id === serviceId ? { ...s, status } : s)),
//             }));
//             await refresh();
//         } catch (e) {
//             console.error(e);
//         }
//     };

//     const handleChangeDocument = async (serviceId, doc, patch) => {
//         try {
//             await updateServiceDocument(id, serviceId, doc.id || doc._id, patch, {
//                 firstName: user.firstName,
//                 lastName: user.lastName,
//             });
//             // optimistic
//             setSubmission((prev) => ({
//                 ...prev,
//                 services: prev.services.map((s) => {
//                     if (!(s._id === serviceId || s.id === serviceId)) return s;
//                     const docs = (s.documents || []).map((d) =>
//                         d.id === (doc.id || doc._id) || d._id === (doc.id || doc._id) || d.path === doc.path
//                             ? { ...d, ...patch }
//                             : d
//                     );
//                     return { ...s, documents: docs };
//                 }),
//             }));
//         } catch (e) {
//             console.error(e);
//         }
//     };

//     const handleAddServiceDoc = async (serviceId, file) => {
//         const formData = new FormData();
//         formData.append("adminFile", file);
//         formData.append("admin", JSON.stringify({ firstName: user.firstName, lastName: user.lastName }));
//         await addServiceDocument(id, serviceId, formData);
//         await refresh();
//     };

//     const handleDeleteServiceDoc = async (serviceId, doc) => {
//         await deleteServiceDocument(id, serviceId, doc.id || doc._id, {
//             firstName: user.firstName,
//             lastName: user.lastName,
//         });
//         await refresh();
//     };

//     const handleDownloadInvoice = async () => {
//         try {
//             await getTransactionPdf(submission._id); // replace with real transaction id if needed
//         } catch (e) {
//             console.error(e);
//         }
//     };

//     if (!submission) return <div className="p-6">Chargement…</div>;

//     const { customer = {}, billing = {}, transactionNumber, societyPrefix } = submission;

//     return (
//         <div className="mx-auto max-w-6xl space-y-6 p-4">
//             {/* Back */}
//             <div className="mb-2 flex items-center gap-2">
//                 <Link to="/" className="flex items-center gap-2 hover:underline">← Retour</Link>
//             </div>

//             {/* Header dossier */}
//             <div className="flex flex-wrap items-center justify-between gap-3">
//                 <div>
//                     <h1 className="text-2xl font-bold">
//                         Dossier n°{societyPrefix ? `${societyPrefix}-` : ""}{transactionNumber}
//                     </h1>
//                     <p className="text-sm text-gray-500">
//                         Créé le {moment(submission.createdAt).format("DD/MM/YYYY à HH:mm")}
//                         {` • Client ${customer.firstname || ""} ${customer.lastname || ""}`}
//                         {lastHandler ? ` • Traité par ${lastHandler}` : ""}
//                     </p>
//                 </div>
//                 <GlobalBadge services={services} />
//             </div>

//             {/* Meta (3 columns) */}
//             <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
//                 <Section
//                     title="Informations de l'utilisateur"
//                     right={
//                         <Link to={"/user/" + customer.email}>
//                             <IconButton title="Voir toutes les demandes">
//                                 <FiExternalLink className="h-3 w-3" /> Tout voir
//                             </IconButton>
//                         </Link>
//                     }
//                 >
//                     <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
//                         <dt className="text-gray-500">Nom</dt><dd className="font-medium">{customer.lastname}</dd>
//                         <dt className="text-gray-500">Prénom</dt><dd className="font-medium">{customer.firstname}</dd>
//                         <dt className="text-gray-500">Email</dt><dd className="font-medium">{customer.email}</dd>
//                         <dt className="text-gray-500">Téléphone</dt><dd className="font-medium">{customer.phone}</dd>
//                         <dt className="text-gray-500">Adresse</dt><dd className="font-medium">{billing.address}</dd>
//                         <dt className="text-gray-500">Pays</dt><dd className="font-medium">{billing.country}</dd>
//                     </dl>
//                 </Section>

//                 <Section title="Services achetés" right={<span className="text-sm text-gray-500">{services.length} prestations</span>}>
//                     <ul className="space-y-2">
//                         {services.map((s) => (
//                             <li key={s._id || s.id} className="flex items-center justify-between rounded-xl border p-2">
//                                 <div className="truncate">
//                                     <div className="truncate text-sm font-medium">{(s.title && (s.title.fr || s.title)) || s.title}</div>
//                                     <div className="text-xs text-gray-500">{(s.price || 0).toFixed(2)} €</div>
//                                 </div>
//                                 <StatusBadge status={s.status || "pending"} />
//                             </li>
//                         ))}
//                     </ul>
//                 </Section>

//                 <Section
//                     title="Facture"
//                     right={<IconButton title="Télécharger" onClick={handleDownloadInvoice}>⬇️ PDF</IconButton>}
//                 >
//                     <div className="text-sm">
//                         <div className="mb-1 flex items-center justify-between">
//                             <span className="text-gray-500">Référence</span>
//                             <span className="font-medium">{transactionNumber ?? "—"}</span>
//                         </div>
//                     </div>
//                 </Section>
//             </div>

//             {/* Prestations (FULL WIDTH) */}
//             <Section title="Validation par prestation">
//                 <div className="space-y-4">
//                     {services.map((service) => (
//                         <ServiceCard
//                             key={service._id || service.id}
//                             service={service}
//                             clientEmail={customer.email}
//                             onChangeStatus={(status) => handleChangeServiceStatus(service._id || service.id, status)}
//                             onChangeDoc={(doc, patch) => handleChangeDocument(service._id || service.id, doc, patch)}
//                             onAddDoc={(file) => handleAddServiceDoc(service._id || service.id, file)}
//                             onDeleteDoc={(doc) => handleDeleteServiceDoc(service._id || service.id, doc)}
//                         />
//                     ))}
//                 </div>
//             </Section>

//             {/* Commentaire interne (FULL WIDTH) */}
//             <Section title="Commentaire interne">
//                 <textarea
//                     className="w-full resize-none rounded-2xl border px-3 py-2"
//                     rows={5}
//                     value={comment}
//                     onChange={(e) => setComment(e.target.value)}
//                     placeholder="Ajouter un commentaire visible par l'équipe uniquement…"
//                 />
//                 <div className="mt-3 flex justify-end">
//                     <button
//                         onClick={handleSaveComment}
//                         disabled={!comment}
//                         className={cx(
//                             "rounded-xl px-4 py-2 text-sm font-semibold text-white",
//                             comment ? "bg-gray-900 hover:bg-black" : "bg-gray-400 cursor-not-allowed"
//                         )}
//                     >
//                         Sauvegarder
//                     </button>
//                 </div>
//             </Section>
//         </div>
//     );
// }